import { useDebounce } from "@uidotdev/usehooks";
import { useAtom, useAtomValue } from "jotai";
import { DateTime } from "luxon";
import { useMemo, useState } from "react";
import { emissionPageStateFamily, selectionContextAtom } from "../../../state";
import { TimelineBlock } from "../base";
import { useRollingAveragesData } from "./hooks";
import { RollingAveragesPlot } from "./plot";

interface RollingAveragesTimelineBlockProps {
    siteId: string;
}

export const RollingAveragesTimelineBlock = (
    props: RollingAveragesTimelineBlockProps,
) => {
    const { dateFrom, dateTo } = useAtomValue(
        emissionPageStateFamily(props.siteId),
    );
    const siteFilters = useMemo(
        () => ({ dateFrom, dateTo }),
        [dateFrom, dateTo],
    );
    const [selectionContext] = useAtom(selectionContextAtom);
    const [hoverContext, setHoverContext] = useState<{ x: Date; y: number }>();
    const [isExpanded, setIsExpanded] = useState(false);

    // Debounce date range
    const debouncedDateRange = useDebounce(
        useMemo(() => {
            let dateBefore = siteFilters.dateTo;
            if (selectionContext.dateRange?.end) {
                dateBefore = selectionContext.dateRange?.end;
            }
            let dateAfter = siteFilters.dateFrom;
            if (selectionContext.dateRange?.start) {
                dateAfter = selectionContext.dateRange.start;
            }
            return { dateBefore, dateAfter };
        }, [
            siteFilters.dateFrom,
            siteFilters.dateTo,
            selectionContext.dateRange,
        ]),
        100,
    );

    // Rolling averages are daily data - allow up to 365 days
    const dateRangeToLoad = useMemo(() => {
        const start = DateTime.fromJSDate(debouncedDateRange.dateAfter);
        const end = DateTime.fromJSDate(debouncedDateRange.dateBefore);
        const durationInDays = end.diff(start, "days").days;

        if (durationInDays > 365) {
            return;
        }

        return {
            start: start.toJSDate(),
            end: end.toJSDate(),
        };
    }, [debouncedDateRange]);

    const rollingAveragesData = useRollingAveragesData({
        siteId: dateRangeToLoad ? props.siteId : undefined,
        startDate: dateRangeToLoad?.start,
        endDate: dateRangeToLoad?.end,
    });

    return (
        <TimelineBlock
            open={isExpanded}
            onOpenChange={setIsExpanded}
            initiallyOpen={false}
            message={
                !dateRangeToLoad ? (
                    <div className="flex items-center justify-center gap-2">
                        Pick a date range under 365 days to load rolling
                        averages
                    </div>
                ) : undefined
            }
            title="LongPath Rolling Averages"
            loading={dateRangeToLoad && rollingAveragesData.isLoading}
            previewPoints={[]}
            dataDateRange={
                selectionContext.dateRange
                    ? selectionContext.dateRange
                    : {
                          start: siteFilters.dateFrom,
                          end: siteFilters.dateTo,
                      }
            }
        >
            <div className="flex min-h-64">
                <div className="p-1 h-full w-[200px]">
                    <div className="flex flex-col gap-0.5">
                        <p className="text-neutral-500">Plot data</p>
                        <p>
                            Daily, 7-day, and 90-day rolling average emission
                            rates (kg/hr).
                        </p>
                    </div>

                    {hoverContext && (
                        <div className="mt-2 text-xs text-ae-slate-600">
                            Cursor at:
                            <br />
                            Date:{" "}
                            {DateTime.fromJSDate(hoverContext.x)
                                .toUTC()
                                .toFormat("MMM dd, yyyy")}{" "}
                            UTC
                            <br />
                            Rate: {hoverContext.y.toFixed(2)} kg/hr
                        </div>
                    )}
                </div>
                <div className="flex-1 relative">
                    <RollingAveragesPlot
                        data={rollingAveragesData.data}
                        dataDateRange={debouncedDateRange}
                        setHoverContext={setHoverContext}
                    />
                </div>
            </div>
        </TimelineBlock>
    );
};
